import React, { ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import { usePageTracking } from '../../../../../hooks';
import {
	AnalyticsPageCategory,
	AnalyticsPageType
} from '../../../../../models';
import {
	Chapter,
	Licence,
	AppState,
	AppThunkDispatch,
	ChapterActions,
	fetchChapter,
	loadDefaultChapter,
	createChapter,
	updateChapter,
	fetchAllLicences
} from '../../../../../redux';
import { BackButton, PageContainer } from '../../../../molecules';
import { ChapterManagerForm } from '../../../../organisms';

export interface IChapterManagerPageProps {
	chapter: Chapter;
	licences: Licence[];
	fetchChapter: (id: string) => void;
	fetchDefaultChapter: () => void;
	createChapter: (chapter: Chapter) => void;
	updateChapter: (chapter: Chapter) => void;
	fetchLicences: () => void;
}

export interface IChapterManagerRouteParams {
	chapterId?: string;
}

function ChapterManagerPageView(
	props: IChapterManagerPageProps & IChapterManagerRouteParams
): ReactElement {
	const {
		chapterId,
		chapter,
		licences,
		fetchChapter,
		fetchDefaultChapter,
		createChapter,
		updateChapter,
		fetchLicences
	} = props;

	usePageTracking(
		AnalyticsPageCategory.ContentManager,
		AnalyticsPageType.Chapter,
		chapterId
	);

	useEffect(() => {
		fetchLicences();
		if (chapterId && chapterId !== chapter.id) {
			fetchChapter(chapterId);
		} else if (!chapterId) {
			fetchDefaultChapter();
		}
	}, []);

	function hasLoaded(): boolean {
		if (chapterId === chapter.id || !chapterId) {
			return true;
		}
		return false;
	}

	function handleSave(chapter: Chapter): void {
		if (chapter.id) {
			updateChapter(chapter);
		} else {
			createChapter(chapter);
		}
	}

	return (
		<PageContainer
			hasLoaded={hasLoaded()}
			id="chapter-manager-page"
			smallPadding
		>
			<BackButton />
			<ChapterManagerForm
				chapter={chapter}
				licences={licences}
				saveChapter={handleSave}
			/>
		</PageContainer>
	);
}

function mapStateToProps(
	state: AppState,
	ownProps: IChapterManagerRouteParams
) {
	return {
		chapterId: ownProps.chapterId,
		chapter: state.chapters.chapter,
		licences: state.licences.licences
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ChapterActions>) {
	return {
		fetchChapter: (id: string) => dispatch(fetchChapter(id)),
		fetchDefaultChapter: () => dispatch(loadDefaultChapter()),
		createChapter: (chapter: Chapter) => dispatch(createChapter(chapter)),
		updateChapter: (chapter: Chapter) => dispatch(updateChapter(chapter)),
		fetchLicences: () => dispatch(fetchAllLicences())
	};
}

export const ChapterManagerPage = connect(
	mapStateToProps,
	mapDispatchToProps
)(ChapterManagerPageView);
